import { useState } from "react";
import { DatePicker } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";

const { RangePicker } = DatePicker;

export const DATE_FORMAT = "DD/MM/YYYY";

const PRESETS: { label: string; getValue: () => [Moment, Moment] }[] = [
  {
    label: "Today",
    getValue: () => [moment().startOf("day"), moment().endOf("day")],
  },
  {
    label: "Yesterday",
    getValue: () => [
      moment().subtract(1, "day").startOf("day"),
      moment().subtract(1, "day").endOf("day"),
    ],
  },
  {
    label: "This week",
    getValue: () => [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
  },
  {
    label: "Last week",
    getValue: () => [
      moment().subtract(1, "week").startOf("isoWeek"),
      moment().subtract(1, "week").endOf("isoWeek"),
    ],
  },
  {
    label: "Past two weeks",
    getValue: () => [
      moment().subtract(2, "weeks").startOf("isoWeek"),
      moment().endOf("isoWeek"),
    ],
  },
  {
    label: "This month",
    getValue: () => [moment().startOf("month"), moment().endOf("month")],
  },
  {
    label: "Last month",
    getValue: () => [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "This year",
    getValue: () => [moment().startOf("year"), moment().endOf("year")],
  },
  {
    label: "Last year",
    getValue: () => [
      moment().subtract(1, "year").startOf("year"),
      moment().subtract(1, "year").endOf("year"),
    ],
  },
];

export const findPresetLabel = (start: Moment, end: Moment): string => {
  const fmt = DATE_FORMAT;
  return (
    PRESETS.find(
      (p) =>
        p.getValue()[0].format(fmt) === start.format(fmt) &&
        p.getValue()[1].format(fmt) === end.format(fmt),
    )?.label ?? ""
  );
};

interface PresetsPanelProps {
  activePreset: string;
  onSelect: (preset: (typeof PRESETS)[number]) => void;
}

const PresetsPanel = ({ activePreset, onSelect }: PresetsPanelProps) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      borderRight: "1px solid #f0f0f0",
      padding: "8px 0",
      minWidth: 140,
    }}
  >
    {PRESETS.map((preset) => (
      <button
        key={preset.label}
        onMouseDown={(e) => {
          e.preventDefault();
          onSelect(preset);
        }}
        style={{
          background: activePreset === preset.label ? "#1890ff" : "transparent",
          color: activePreset === preset.label ? "#fff" : "inherit",
          border: "none",
          padding: "6px 16px",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 14,
          whiteSpace: "nowrap",
        }}
      >
        {preset.label}
      </button>
    ))}
  </div>
);

interface ReportRangePickerProps {
  value: [Moment, Moment];
  onChange: (range: [Moment, Moment]) => void;
  initialActivePreset?: string;
}

const ReportRangePicker = ({
  value,
  onChange,
  initialActivePreset = "This week",
}: ReportRangePickerProps) => {
  const [activePreset, setActivePreset] = useState<string>(initialActivePreset);

  const handlePresetClick = (preset: (typeof PRESETS)[number]) => {
    onChange(preset.getValue());
    setActivePreset(preset.label);
  };

  const handleRangeChange = (values: any) => {
    if (values && values[0] && values[1]) {
      onChange([values[0], values[1]]);
      setActivePreset("");
    }
  };

  return (
    <RangePicker
      value={value}
      onChange={handleRangeChange}
      format={DATE_FORMAT}
      suffixIcon={<CalendarOutlined />}
      allowClear={false}
      disabledDate={(current) => current && current.isAfter(moment(), "day")}
      panelRender={(panel) => (
        <div style={{ display: "flex" }}>
          <PresetsPanel
            activePreset={activePreset}
            onSelect={handlePresetClick}
          />
          {panel}
        </div>
      )}
    />
  );
};

export default ReportRangePicker;
